import React, { useEffect, useRef, useState } from 'react';
import ReactDOM from 'react-dom';
import { AlertTriangle, CheckCircle2, Home, X } from 'lucide-react';
import MensagemAvisoMarkdown from '../../src/components/MensagemAvisoMarkdown';
import ExemploBoletoUnico from './ExemploBoletoUnico';
import {
  enderecoCurtoUc,
  MOTIVOS_ACEITE_RECUSADO,
  ROTULOS_SITUACAO_PORTAL,
  selecaoInicial,
  textoBotaoAceite,
  type LinhaUcPortal,
} from '../../src/lib/boletoUnico/aceitePortal';
import type { AvisoAceite, ResultadoAceite } from '../../src/hooks/useConviteBoletoUnico';

// Convite de adesão ao boleto único (tarefa #5575). Mesmo esqueleto do AvisoModal — bottom-sheet
// no mobile, modal no desktop, rolar até o fim antes de habilitar, trava de foco — com duas
// diferenças que são o motivo de existir um componente próprio:
//
//   1. A adesão é POR UNIDADE (texto da cláusula). O cliente escolhe quais UCs, e cada uma vira
//      um aceite separado no banco. Com várias UCs nenhuma vem marcada: marcar por ele seria
//      decidir por ele. Das 519 clientes aptos em 14/09/2026, 72 têm mais de uma UC.
//   2. Nunca é bloqueante. Sempre dá para fechar — "é um aviso, não um muro".
//
// O botão não grava nada pelo navegador: chama a função `aceitar_boleto_unico_portal`, que
// confere posse e elegibilidade de novo e preenche data, versão do texto, IP e navegador.

interface Props {
  aviso: AvisoAceite;
  linhas: LinhaUcPortal[];
  onAceitar: (ucIds: number[]) => Promise<ResultadoAceite>;
  onFechar: () => void;
}

const dataHora = (iso: string): string => {
  const d = new Date(iso);
  return Number.isNaN(d.getTime())
    ? ''
    : `${d.toLocaleDateString('pt-BR')} às ${d.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })}`;
};

const AceiteBoletoUnicoModal = ({ aviso, linhas, onAceitar, onFechar }: Props) => {
  // A lista do modal é congelada na abertura: se o convite recarregar por trás (outra aba,
  // o próprio aceite), as UCs não mudam de lugar embaixo do dedo de quem está escolhendo.
  const [linhasFixas] = useState(linhas);
  const aderiveis = linhasFixas.filter(l => l.situacao === 'pode_aderir');
  const outras = linhasFixas.filter(l => l.situacao !== 'pode_aderir');

  const [selecionadas, setSelecionadas] = useState<Set<number>>(() => new Set(selecaoInicial(linhasFixas)));
  // `rolouAteOFim` é permanente (o cliente de fato chegou ao fim). `cabeSemRolar` NÃO é: é
  // remedido a cada mudança de tamanho. Ver o comentário do ResizeObserver abaixo.
  const [rolouAteOFim, setRolouAteOFim] = useState(false);
  const [cabeSemRolar, setCabeSemRolar] = useState(false);
  const lido = rolouAteOFim || cabeSemRolar;
  const [concordo, setConcordo] = useState(false);
  const [enviando, setEnviando] = useState(false);
  const [erro, setErro] = useState<string | null>(null);
  const [resultado, setResultado] = useState<ResultadoAceite | null>(null);

  const corpoRef = useRef<HTMLDivElement>(null);
  const dialogRef = useRef<HTMLDivElement>(null);
  // Quem abriu o modal clicando em "Ver condições e aceitar" (ou tabulando até lá) precisa
  // voltar pro mesmo lugar ao fechar — sem isto o foco cai para o <body> e um usuário de
  // teclado/leitor de tela perde a posição na página.
  const elementoAnteriorRef = useRef<HTMLElement | null>(null);

  useEffect(() => {
    elementoAnteriorRef.current = document.activeElement as HTMLElement | null;
    dialogRef.current?.focus();
    return () => { elementoAnteriorRef.current?.focus?.(); };
  }, []);

  // Depois do aceite o conteúdo do diálogo muda inteiro (o botão que tinha foco some do DOM).
  // Sem levar o foco de volta pro diálogo, ele cai pro <body> e quem navega por teclado perde
  // a posição bem no momento mais importante — confirmar o que realmente aconteceu.
  useEffect(() => {
    if (resultado) dialogRef.current?.focus();
  }, [resultado]);

  // Texto curto que cabe sem rolagem nunca dispararia o onScroll — sem tratar isso o botão
  // ficaria travado numa tela grande. Mas NÃO dá para medir uma vez só na montagem: o Tailwind
  // deste projeto vem por CDN e gera o CSS de classes novas alguns milissegundos DEPOIS de elas
  // entrarem na tela. Na montagem o `max-h-[92vh]` ainda não existe, a área rolável tem a altura
  // do texto inteiro e "cabe sem rolar" dava sempre verdadeiro — no teste de 14/09/2026 a caixa
  // nasceu liberada com 1.066 px de texto numa área de 460 px. Então medimos de novo a cada
  // mudança de tamanho, e "cabe" só vale enquanto de fato couber.
  useEffect(() => {
    const el = corpoRef.current;
    if (!el) return;
    const medir = () => setCabeSemRolar(el.scrollHeight - el.clientHeight < 24);
    medir();
    const observador = new ResizeObserver(medir);
    observador.observe(el);
    // Todos os filhos, não só o primeiro: a cláusula, o exemplo do boleto e a lista de
    // unidades crescem em momentos diferentes (a imagem do exemplo chega da rede depois).
    // Observar só o primeiro deixaria o "cabe sem rolar" medido numa altura que já mudou.
    Array.from(el.children).forEach(filho => observador.observe(filho));
    return () => observador.disconnect();
  }, [resultado]);

  // Se a caixa voltou a ficar travada (a área encolheu depois que o CSS chegou), o "concordo"
  // marcado nesse meio-tempo não vale: o cliente não leu.
  useEffect(() => {
    if (!lido && concordo) setConcordo(false);
  }, [lido, concordo]);

  const aoRolar = () => {
    const el = corpoRef.current;
    if (!el || rolouAteOFim) return;
    if (el.scrollHeight - el.scrollTop - el.clientHeight < 24) setRolouAteOFim(true);
  };

  const alternar = (ucId: number) => {
    setErro(null);
    setSelecionadas(prev => {
      const nova = new Set(prev);
      if (nova.has(ucId)) nova.delete(ucId); else nova.add(ucId);
      return nova;
    });
  };

  const podeAceitar = lido && concordo && selecionadas.size > 0 && !enviando;

  const aceitar = async () => {
    if (!podeAceitar) return;
    setEnviando(true);
    setErro(null);
    try {
      setResultado(await onAceitar([...selecionadas]));
    } catch (e: any) {
      setErro(e?.message || 'Não foi possível registrar o aceite agora. Tente de novo em instantes.');
    } finally {
      setEnviando(false);
    }
  };

  // Trava de foco igual à do AvisoModal: Tab e Shift+Tab ficam dentro do diálogo. Aqui Esc
  // sempre fecha, porque o convite nunca é bloqueante.
  const aoTeclar = (e: React.KeyboardEvent) => {
    if (e.key === 'Escape') { if (!enviando) onFechar(); return; }
    if (e.key !== 'Tab') return;
    const focaveis = dialogRef.current?.querySelectorAll<HTMLElement>(
      'button:not([disabled]), input:not([disabled]), a[href], [tabindex]:not([tabindex="-1"])'
    );
    if (!focaveis || focaveis.length === 0) return;
    const primeiro = focaveis[0];
    const ultimo = focaveis[focaveis.length - 1];
    if (e.shiftKey && document.activeElement === primeiro) { e.preventDefault(); ultimo.focus(); }
    else if (!e.shiftKey && document.activeElement === ultimo) { e.preventDefault(); primeiro.focus(); }
  };

  const numeroDa = (ucId: number) => linhasFixas.find(l => l.uc.id === ucId)?.uc.numero_uc || String(ucId);

  return ReactDOM.createPortal(
    <div
      className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/50 backdrop-blur-sm p-0 sm:p-4 animate-fade-in"
      onClick={() => { if (!enviando) onFechar(); }}
    >
      <div
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby="aceite-bu-titulo"
        tabIndex={-1}
        onKeyDown={aoTeclar}
        onClick={e => e.stopPropagation()}
        className="bg-white rounded-t-3xl sm:rounded-2xl w-full max-w-3xl shadow-2xl overflow-hidden max-h-[92vh] flex flex-col outline-none"
      >
        <div className="px-5 sm:px-6 pt-6 pb-4 border-b border-gray-100">
          <div className="flex items-start justify-between gap-3">
            <span className="inline-flex text-xs font-bold px-2.5 py-1 rounded-full bg-secondary/15 text-secondary-dark">
              Boleto único · opcional
            </span>
            <button
              onClick={onFechar}
              disabled={enviando}
              aria-label="Fechar"
              className="p-2 -mr-2 -mt-2 rounded-full text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-colors shrink-0 disabled:opacity-40"
            >
              <X size={20} />
            </button>
          </div>
          <h2 id="aceite-bu-titulo" className="text-xl font-bold text-gray-900 mt-3">
            {resultado
              ? (resultado.aceitas.length > 0 ? 'Aceite registrado' : 'Nada foi registrado')
              : aviso.titulo}
          </h2>
        </div>

        {resultado ? (
          // ─────────────── Depois do aceite ───────────────
          <div className="px-5 sm:px-6 py-6 overflow-y-auto flex-1 space-y-5" aria-live="polite">
            {resultado.aceitas.length > 0 && (
              <div className="rounded-2xl bg-primary/5 border border-primary/15 p-4">
                <p className="flex items-center gap-2 font-bold text-gray-900">
                  <CheckCircle2 size={20} className="text-primary shrink-0" />
                  {resultado.aceitas.length === 1 ? 'Recebemos o seu aceite' : `Recebemos o seu aceite para ${resultado.aceitas.length} unidades`}
                </p>
                <ul className="mt-3 space-y-1.5 text-sm text-gray-700">
                  {resultado.aceitas.map(a => (
                    <li key={a.uc_id}>UC <strong>{numeroDa(a.uc_id)}</strong> — {dataHora(a.aceito_em)}</li>
                  ))}
                </ul>
              </div>
            )}

            {resultado.ignoradas.length > 0 && (
              <div className="rounded-2xl bg-amber-50 border border-amber-200 p-4 text-sm text-amber-900">
                <p className="font-bold">Não registramos para:</p>
                <ul className="mt-2 space-y-1">
                  {resultado.ignoradas.map(i => (
                    <li key={i.uc_id}>UC {numeroDa(i.uc_id)} — {MOTIVOS_ACEITE_RECUSADO[i.motivo] || 'não foi possível registrar'}</li>
                  ))}
                </ul>
              </div>
            )}

            {resultado.aceitas.length > 0 ? (
              // A dúvida mais provável depois de aceitar é "já posso parar de pagar a
              // Equatorial?". Responder aqui evita conta atrasada na distribuidora.
              <div className="text-sm text-gray-700 space-y-2">
                <p className="font-bold text-gray-900">E agora?</p>
                <p>A Vigor confere cada unidade e avisa por WhatsApp quando ela estiver liberada no boleto único.</p>
                <p>
                  <strong>Até esse aviso, nada muda:</strong> continue pagando a fatura da Equatorial e o boleto da Vigor como hoje.
                </p>
                {resultado.aceitas.some(a => linhasFixas.find(l => l.uc.id === a.uc_id)?.debitoAutomatico) && (
                  <p className="text-amber-800">
                    Lembre de cancelar o débito automático da conta de luz no seu banco e enviar o comprovante para a Vigor —
                    sem isso a unidade não pode ser liberada.
                  </p>
                )}
              </div>
            ) : (
              // Nenhuma unidade entrou (ex.: convite foi despublicado ou já tinha aceite
              // registrado por outra aba entre a abertura do modal e o clique). Sem isto o
              // cliente lê "Aceite registrado" + "E agora?" achando que aderiu de verdade.
              <div className="text-sm text-gray-700 space-y-2">
                <p>Nenhuma unidade foi registrada agora. Continue pagando a Equatorial e o boleto da Vigor como hoje.</p>
                <p>Se o motivo acima não fizer sentido, fale com a Vigor antes de tentar de novo.</p>
              </div>
            )}

            <button
              onClick={onFechar}
              className="w-full py-3.5 px-4 rounded-full text-sm font-bold text-white bg-primary hover:bg-primary/90 transition-all"
            >
              Entendi
            </button>
          </div>
        ) : (
          <>
            {/* ─────────────── Cláusula + unidades ─────────────── */}
            <div
              ref={corpoRef}
              onScroll={aoRolar}
              tabIndex={0}
              aria-label="Condições do boleto único e suas unidades — role até o fim para habilitar o aceite"
              className="px-5 sm:px-6 py-5 overflow-y-auto flex-1 text-sm text-gray-700 leading-relaxed focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/40"
            >
              <MensagemAvisoMarkdown value={aviso.mensagem} variante="portal" />

              <ExemploBoletoUnico />

              <fieldset className="mt-6">
                <legend className="text-base font-bold text-gray-900">
                  {aderiveis.length === 1 ? 'Sua unidade' : 'Escolha as unidades que vão aderir'}
                </legend>
                {aderiveis.length > 1 && (
                  <p className="text-xs text-gray-500 mt-1">Cada unidade é uma adesão separada. Marque só as que você quer.</p>
                )}

                <ul className="mt-3 space-y-2">
                  {aderiveis.map(({ uc, debitoAutomatico }) => {
                    const marcada = selecionadas.has(uc.id);
                    const endereco = enderecoCurtoUc(uc);
                    return (
                      <li key={uc.id}>
                        <label
                          className={`flex items-start gap-3 rounded-xl border p-3.5 cursor-pointer transition-colors ${
                            marcada ? 'border-primary/40 bg-primary/5' : 'border-gray-200 hover:border-gray-300'
                          }`}
                        >
                          <input
                            type="checkbox"
                            checked={marcada}
                            onChange={() => alternar(uc.id)}
                            className="mt-0.5 w-5 h-5 rounded border-gray-300 text-primary focus:ring-primary/30"
                          />
                          <span className="min-w-0 flex-1">
                            <span className="flex items-center gap-1.5 font-bold text-gray-900">
                              <Home size={15} className="text-primary shrink-0" aria-hidden="true" /> UC {uc.numero_uc}
                            </span>
                            {endereco && <span className="block text-xs text-gray-500 mt-0.5">{endereco}</span>}
                            {debitoAutomatico && (
                              <span className="mt-2 flex items-start gap-1.5 text-xs font-semibold text-amber-800 bg-amber-50 border border-amber-200 rounded-lg px-2.5 py-2">
                                <AlertTriangle size={14} className="shrink-0 mt-px" aria-hidden="true" />
                                A última fatura desta unidade mostra débito automático. Cancele no seu banco antes — senão você paga duas vezes.
                              </span>
                            )}
                          </span>
                        </label>
                      </li>
                    );
                  })}
                </ul>

                {outras.length > 0 && (
                  <ul className="mt-4 space-y-1.5 text-xs text-gray-500">
                    {outras.map(({ uc, situacao }) => (
                      <li key={uc.id}>
                        UC {uc.numero_uc} — {ROTULOS_SITUACAO_PORTAL[situacao as 'aceito_aguardando' | 'ativo'].texto.toLowerCase()}
                      </li>
                    ))}
                  </ul>
                )}
              </fieldset>
            </div>

            <div className="px-5 sm:px-6 py-5 border-t border-gray-100 space-y-4">
              <label className={`flex items-start gap-3 text-sm ${lido ? 'text-gray-700' : 'text-gray-400'}`}>
                <input
                  type="checkbox"
                  checked={concordo}
                  disabled={!lido}
                  onChange={e => setConcordo(e.target.checked)}
                  className="mt-0.5 w-5 h-5 rounded border-gray-300 text-primary focus:ring-primary/30 disabled:opacity-50"
                />
                <span>
                  Li as condições e quero aderir ao boleto único {aderiveis.length === 1 ? 'nesta unidade' : 'nas unidades marcadas'}.
                  {!lido && <span className="block text-xs mt-0.5">Role até o fim para habilitar.</span>}
                </span>
              </label>

              <span aria-live="polite" className="sr-only">
                {lido && 'Texto lido até o fim. A confirmação está liberada.'}
              </span>

              {erro && (
                <div role="alert" className="text-sm text-red-700 bg-red-50 border border-red-200 rounded-xl px-3 py-2.5 space-y-2">
                  <p>{erro}</p>
                  {/* A função devolve esse texto quando o convite foi despublicado ou mudou de versão
                      entre a abertura do modal e o clique — "recarregue a página" só ajuda se der
                      pra fazer isso sem sair da tela procurando F5. */}
                  {erro.includes('Recarregue') && (
                    <button
                      type="button"
                      onClick={() => window.location.reload()}
                      className="text-sm font-bold text-red-800 underline underline-offset-2"
                    >
                      Recarregar página
                    </button>
                  )}
                </div>
              )}

              <button
                onClick={aceitar}
                disabled={!podeAceitar}
                aria-busy={enviando}
                className="w-full flex justify-center items-center gap-2 py-3.5 px-4 rounded-full text-sm font-bold text-white bg-primary hover:bg-primary/90 disabled:bg-gray-300 disabled:cursor-not-allowed transition-all"
              >
                {enviando ? 'Registrando...' : textoBotaoAceite(selecionadas.size)}
              </button>

              <button
                onClick={onFechar}
                disabled={enviando}
                className="w-full text-xs font-semibold text-gray-400 hover:text-gray-600 transition-colors disabled:opacity-40"
              >
                Agora não — continuar pagando separado
              </button>
            </div>
          </>
        )}
      </div>
    </div>,
    document.body
  );
};

export default AceiteBoletoUnicoModal;
